import * as React from 'react'
import {
  Box,
  Form,
  Button,
  Typography,
  Loader,
  Snackbar,
} from 'cs-kevlar-react'
import { FormikSelect } from '../FormikSelect'
import { FormikTextField } from '../FormikTextField'
import { Formik } from 'formik'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Grid } from 'cs-kevlar-react'
import * as Yup from 'yup'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import { QueryableTargetField, DocumentType, ApiResult } from '../../api'
import { getDocumentTypeLabel, getAllEnumOptions } from 'utils'
import { useQueryDocumentApi, usePageNotifications, useLogger } from 'hooks'
import {
  getAllQueryableTargetFieldsForDocumentType,
  getDefaultQueryableTargetFieldForDocumentType,
} from 'utils/enums/queryableTargetField'
import { JsonInput } from 'components/JsonInput'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
    gridItem: {
      width: '100%',
    },
    footer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
    },
    actionButton: {
      marginLeft: 'auto',
      marginBottom: theme.spacing(4),
    },
    hasAllPermission: {
      textAlign: 'center',
      color: theme.palette.success.dark,
    },
    hasPermissions: {
      color: theme.palette.success.dark,
    },
    missingPermissions: {
      color: theme.palette.error.dark,
    },
    key: {
      color: theme.palette.neutralButton.main,
    },
    marginTop: {
      marginTop: theme.spacing(8),
    },
    error: {
      textAlign: 'center',
      color: theme.palette.error.dark,
    },
  }),
)

export const QueryDocumentsFormSchema = Yup.object().shape({
  type: Yup.string().required('The type field is required.'),
  id: Yup.string().required('The id field is required.'),
})

const documentTypeOptions: Option[] = getAllEnumOptions(DocumentType)

interface QueryDocumentsProps {
  customerAgencyId: string
}

export const QueryDocuments: React.FunctionComponent<QueryDocumentsProps> = ({
  customerAgencyId,
}) => {
  if (customerAgencyId === null) return null

  const classes = useStyles({})
  const { logError } = useLogger()

  const { queryDocument } = useQueryDocumentApi()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const [results, setResults] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [documentType, setDocumentType] = React.useState<DocumentType>(
    DocumentType.Incident,
  )

  const initialValues = {
    type: DocumentType.Incident,
    field: getDefaultQueryableTargetFieldForDocumentType(DocumentType.Incident),
    id: '',
  }

  const onSubmit = (values: any, actions: any) => {
    // Clear any previous result/error
    setResults(null)
    setError(null)

    queryDocument(customerAgencyId, values.type, values.field, values.id)
      .then(response => {
        if (!response) return

        if (response.status === 200) {
          if (!response.data.isSuccess || response.data.errorMessage !== null)
            setError(response.data.errorMessage)

          setResults(response.data.data)
        } else {
          setResults(null)
        }
      })
      .catch(error => {
        logError(error)
        setResults(null)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error querying the document.',
        })
      })
      .then(() => {
        actions.setSubmitting(false)
      })
  }

  const fieldTypeOptions = getAllQueryableTargetFieldsForDocumentType(
    documentType,
  )

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        validationSchema={QueryDocumentsFormSchema}
        onSubmit={onSubmit}
      >
        {formikProps => (
          <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
            <Grid
              container
              direction="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              <Grid item className={classes.gridItem}>
                <FormikSelect
                  id="QueryDocuments::DocumentType"
                  formikProps={formikProps}
                  label="Type"
                  fieldName="type"
                  fullWidth
                  options={documentTypeOptions}
                  onChange={(event, newValue) => {
                    setDocumentType(event.target.value as DocumentType)
                    formikProps.setFieldValue(
                      'field',
                      getDefaultQueryableTargetFieldForDocumentType(
                        event.target.value as DocumentType,
                      ),
                    )
                    formikProps.handleChange(event)
                  }}
                />
              </Grid>
              <Grid item className={classes.gridItem}>
                <FormikSelect
                  id="QueryDocuments::FieldType"
                  formikProps={formikProps}
                  label="Field"
                  fieldName="field"
                  fullWidth
                  options={fieldTypeOptions}
                />
              </Grid>
              <Grid item className={classes.gridItem}>
                <FormikTextField
                  formikProps={formikProps}
                  fieldName="id"
                  label="Field Value"
                  fullWidth
                  autoComplete="off"
                />
              </Grid>
              <Grid item className={classes.footer}>
                <Button
                  color="primary"
                  type="submit"
                  className={classes.actionButton}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
            {formikProps.isSubmitting && (
              <Loader size="sm" text="Loading Results" />
            )}
            {error !== null && (
              <Typography className={classes.error}>{error}</Typography>
            )}
            {results !== null && (
              <JsonInput json={results} label="" rows={10} />
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
}
